import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HowItWorksRadial } from '../components/HowItWorksRadial';
import { preferencesAPI } from '../api';
import {
  BookOpen,
  Headphones,
  MessageSquare,
  GraduationCap,
  Sparkles,
  ArrowRight,
  FileSearch,
  TrendingUp,
  Globe,
} from 'lucide-react';

interface FeatureItem {
  id: string;
  name: string;
  path: string;
  icon: React.ElementType;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  iconBg: string;
}

const FEATURES: FeatureItem[] = [
  {
    id: 'litscreen',
    name: 'LitScreen',
    path: '/screen',
    icon: FileSearch,
    description: 'Screen and triage incoming literature',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50/80',
    borderColor: 'border-sky-200',
    iconBg: 'bg-sky-500',
  },
 
  {
    id: 'lithub',
    name: 'LitHub',
    path: '/library/shelf',
    icon: BookOpen,
    description: 'Your canonical article library',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50/80',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-500',
  },
  {
    id: 'litstream',
    name: 'LitStream',
    path: '/audio-digests',
    icon: Headphones,
    description: 'Listen to AI audio summaries',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50/80',
    borderColor: 'border-violet-200',
    iconBg: 'bg-violet-500',
  },
  {
    id: 'litclub',
    name: 'LitClub',
    path: '/community',
    icon: MessageSquare,
    description: 'Discuss with specialty peers',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-500',
  },
  {
    id: 'litscholar',
    name: 'LitScholar',
    path: '/litscholar',
    icon: GraduationCap,
    description: 'Deep dive with AI analysis',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50/80',
    borderColor: 'border-rose-200',
    iconBg: 'bg-rose-500',
  },
 {
    id: 'litportal',
    name: 'LitPortal',
    path: '/litportal',
    icon: Globe,
    description: 'Clinical evidence search',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50/80',
    borderColor: 'border-teal-200',
    iconBg: 'bg-teal-500',
  },
];

const FeatureHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const features = FEATURES;

  const [unscreened, setUnscreened] = useState(0);
  const [profileCount, setProfileCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const stats = await preferencesAPI.getProfilesScreenStats();
        setUnscreened(stats.total_unscreened || 0);
        setProfileCount(stats.profiles?.length || 0);
      } catch {}
    })();
  }, []);

  const queueCount = profileCount;

  return (
    <div className="px-4 py-6 sm:py-8 max-w-5xl mx-auto" data-testid="feature-hub">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">
          Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-slate-500 text-sm sm:text-base">What would you like to explore today?</p>
      </div>

      {unscreened > 0 && (
        <button
          type="button"
          onClick={() => navigate('/screen')}
          className="w-full mb-6 flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-shadow group"
          data-testid="unscreened-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm sm:text-base">
                {unscreened} article{unscreened !== 1 ? 's' : ''} waiting for review
              </p>
              <p className="text-blue-100 text-xs sm:text-sm">Continue screening your latest queue</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}

      <div className="mb-4 sm:mb-5">
        <HowItWorksRadial
          variant="compact"
          collapsible
          defaultExpanded={false}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6" data-testid="feature-grid">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <button
              key={feature.id}
              type="button"
              onClick={() => navigate(feature.path)}
              className={`flex flex-col items-center text-center p-4 sm:p-6 min-h-[160px] sm:min-h-[180px] rounded-xl border ${feature.borderColor} ${feature.bgColor} hover:shadow-md transition-shadow`}
              data-testid={`feature-${feature.id}`}
            >
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${feature.iconBg} flex items-center justify-center mb-3 shadow-sm`}>
                <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className={`font-bold text-sm sm:text-base ${feature.color}`}>{feature.name}</h3>
              <p className="text-sm sm:text-base text-slate-600 mt-2 leading-relaxed">{feature.description}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="quick-stats">
        <div className="text-center py-4 px-2 bg-white rounded-xl border border-slate-200">
          <div className="text-2xl font-bold text-slate-800">{queueCount}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">Queue</div>
        </div>
        <div className="text-center py-4 px-2 bg-white rounded-xl border border-slate-200">
          <div className="text-2xl font-bold text-slate-800">{unscreened}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">To Screen</div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/library/shelf')}
          className="text-center py-4 px-2 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all"
        >
          <BookOpen className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">LitHub</div>
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 text-slate-400 text-xs sm:text-sm">
        <Sparkles className="w-3.5 h-3.5" />
        <span>Start with LitScreen to triage your daily literature</span>
      </div>
    </div>
  );
};

export default FeatureHubPage;
